/**
 * LA CONSTRUCTION DE L'ÉLÈVE, REJOUÉE CHEZ LE TUTEUR — et la désignation d'un
 * objet de son tracé (atelier de figures, lot L7, AB#943 ;
 * `docs/pilotage/atelier-figures/CONCEPTION.md` § 4).
 *
 * « Le tuteur fait défiler les étapes de l'élève et voit OÙ la construction a
 * dévié, pas seulement le résultat. » L'atelier garde l'historique de ses
 * commandes pour annuler et rétablir (`construction-reducteur.ts`) ; depuis L7,
 * cet historique part AVEC la tentative (`historiqueConstruction`, additif).
 * Ce module :
 *
 *  - `lireHistorique` : relit un historique reçu — sa FORME seulement ;
 *  - `rejouerHistorique` : le rejoue À REBOURS depuis la construction soumise,
 *    puis à l'endroit, et ne le garde que s'il la redonne EXACTEMENT, chaque
 *    état intermédiaire lisible. Un historique qui ne la redonne pas n'est
 *    jamais cru : le serveur l'écarte (la tentative, elle, reste jugée) ;
 *  - `constructionDeLEleve` : les étapes à montrer, une phrase par commande —
 *    ou, pour un essai d'avant L7, une par objet, dans l'ordre de création ;
 *  - `etapeDeDeviation` : l'étape où l'objet de la première contrainte
 *    manquée (ou l'un de ceux dont il dépend) a pris sa forme finale ;
 *  - `designerObjet`, `objetsDesignables` : « la droite d », « le point C′ » —
 *    ce qu'une note du tuteur ancre (option B, décision 3 du fondateur).
 *
 * Tout est PUR et déterministe : ni modèle, ni horloge, ni base. Rien ici ne
 * touche au verdict — il a été rendu à la soumission, sur le document seul.
 */
import { appliquerCommande, defaireCommande, HISTORIQUE_MAX, } from './construction-reducteur.js';
import { estIdEnonce, griefsDocument, OBJETS_MAX_CONSTRUCTION, referencesDe, } from './construction.js';
import { coordonneesLues, lecteurDeConstruction } from './decrire-geste.js';
/* ─────────────────────────── l'historique reçu ─────────────────────────── */
/** Au plus, comme l'atelier : au-delà, les plus anciennes commandes sont oubliées. */
export const COMMANDES_MAX_HISTORIQUE = HISTORIQUE_MAX;
const estObjet = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const estDefinition = (v) => estObjet(v) && typeof v['type'] === 'string';
const estObjetDeConstruction = (v) => estObjet(v) && typeof v['id'] === 'string' && v['id'] !== '' && estDefinition(v['def']);
/**
 * La FORME d'un historique : une liste de commandes de l'atelier. Rien de plus
 * n'est vérifié ici — `rejouerHistorique` dit s'il redonne la construction.
 * `null` : ce n'en est pas un.
 */
export function lireHistorique(brut) {
    if (!Array.isArray(brut) || brut.length > COMMANDES_MAX_HISTORIQUE)
        return null;
    for (const c of brut) {
        if (!estObjet(c))
            return null;
        switch (c['type']) {
            case 'ajouter':
            case 'partir_de_l_enonce': {
                const objets = c['objets'];
                if (!Array.isArray(objets) ||
                    objets.length === 0 ||
                    objets.length > OBJETS_MAX_CONSTRUCTION ||
                    !objets.every(estObjetDeConstruction)) {
                    return null;
                }
                break;
            }
            case 'deplacer':
                if (typeof c['id'] !== 'string' || !estDefinition(c['avant']) || !estDefinition(c['apres']))
                    return null;
                break;
            case 'modifier': {
                const avant = c['avant'];
                const apres = c['apres'];
                if (typeof c['id'] !== 'string' ||
                    !estObjet(avant) ||
                    !estObjet(apres) ||
                    !estDefinition(avant['def']) ||
                    !estDefinition(apres['def'])) {
                    return null;
                }
                break;
            }
            case 'supprimer': {
                const objets = c['objets'];
                if (!Array.isArray(objets) ||
                    objets.length === 0 ||
                    objets.length > OBJETS_MAX_CONSTRUCTION ||
                    !objets.every((x) => estObjet(x) &&
                        Number.isInteger(x['rang']) &&
                        x['rang'] >= 0 &&
                        estObjetDeConstruction(x['objet']))) {
                    return null;
                }
                break;
            }
            default:
                return null;
        }
    }
    return brut;
}
const memesObjets = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/**
 * Rejoue l'historique : à rebours depuis la construction soumise (`final`),
 * puis à l'endroit. `null` quand il ne la redonne pas exactement, qu'une
 * commande ne s'applique pas, ou qu'un état intermédiaire est illisible —
 * jamais une construction « à peu près ».
 */
export function rejouerHistorique(final, commandes, enonce) {
    if (commandes.length === 0 || commandes.length > COMMANDES_MAX_HISTORIQUE)
        return null;
    let doc = final;
    for (let i = commandes.length - 1; i >= 0; i -= 1) {
        doc = defaireCommande(doc, commandes[i]);
    }
    const depart = doc;
    const etats = [];
    let courant = depart;
    for (const c of commandes) {
        const suivant = appliquerCommande(courant, c);
        if (suivant === null)
            return null;
        etats.push(suivant);
        courant = suivant;
    }
    if (!memesObjets(courant.objets, final.objets))
        return null;
    for (const e of [depart, ...etats]) {
        if (griefsDocument(e, enonce).length > 0)
            return null;
    }
    return { depart, etats, commandes };
}
/* ─────────────────────────── désigner un objet ─────────────────────────── */
/** Ce qui ne se désigne pas comme un objet du tracé : un trait masqué, une mesure, un texte. */
const NON_DESIGNABLES = new Set([
    'mesure_longueur',
    'mesure_angle',
    'texte',
    'table_valeurs',
]);
/** « une droite qui passe par… » → « la droite qui passe par… ». */
function avecArticleDefini(phrase) {
    if (phrase.startsWith('une '))
        return `la ${phrase.slice(4)}`;
    if (phrase.startsWith('un '))
        return `le ${phrase.slice(3)}`;
    return phrase;
}
/**
 * Un objet de la construction, désigné comme on le montrerait du doigt :
 * « le point C », « la droite d », ou — sans nom — « la droite qui passe par
 * (3 ; 2) et (2 ; 3) ». `null` : il n'est pas dans le document.
 */
export function designerObjet(doc, id, enonce) {
    const o = doc.objets.find((x) => x.id === id);
    if (o === undefined)
        return null;
    const { lire, resolue } = lecteurDeConstruction(doc, enonce);
    const r = resolue.objets.get(id);
    if (o.nom !== undefined && o.nom.trim() !== '') {
        const nom = o.nom.trim();
        switch (r?.genre) {
            case 'point':
                return `le point ${nom}`;
            case 'ligne':
                return r.sorte === 'segment'
                    ? `le segment ${nom}`
                    : r.sorte === 'demi_droite'
                        ? `la demi-droite ${nom}`
                        : `la droite ${nom}`;
            case 'cercle':
                return `le cercle ${nom}`;
            case 'polygone':
                return `le polygone ${nom}`;
            case 'vecteur':
                return `le vecteur ${nom}`;
            case 'fonction':
                return `la courbe ${nom}`;
            default:
                return `l’objet ${nom}`;
        }
    }
    if (r?.genre === 'point')
        return `le point ${coordonneesLues(r.p)}`;
    const phrase = lire(o);
    return phrase === '' ? null : avecArticleDefini(phrase);
}
/**
 * Les objets du tracé de l'élève qu'une note peut ancrer, dans l'ordre du
 * document : ni ceux de l'énoncé, ni un trait masqué, ni une mesure, ni un texte.
 */
export function objetsDesignables(doc, enonce) {
    const objets = Array.isArray(doc?.objets) ? doc.objets : [];
    return objets.flatMap((o) => {
        if (estIdEnonce(o.id) || o.masque === true || NON_DESIGNABLES.has(o.def.type))
            return [];
        const libelle = designerObjet(doc, o.id, enonce);
        return libelle === null ? [] : [{ id: o.id, libelle }];
    });
}
const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);
/** Les objets qu'un lot ajouté dit : ceux qu'aucun autre objet du lot ne cite (une droite, pas ses deux points). */
function finauxDuLot(objets) {
    const cites = new Set();
    for (const o of objets)
        for (const ref of referencesDe(o.def))
            cites.add(ref.id);
    const finaux = objets.filter((o) => !cites.has(o.id) && o.masque !== true);
    return finaux.length > 0 ? finaux : objets;
}
function enListe(morceaux) {
    if (morceaux.length <= 1)
        return morceaux[0] ?? '';
    return `${morceaux.slice(0, -1).join(', ')} et ${morceaux[morceaux.length - 1] ?? ''}`;
}
/** La phrase d'une commande, dite sur les documents d'avant et d'après. */
function phraseDeCommande(c, avant, apres, enonce) {
    switch (c.type) {
        case 'partir_de_l_enonce':
            return `Part de la figure de l’énoncé (${c.objets.length} ${c.objets.length > 1 ? 'objets recopiés' : 'objet recopié'}).`;
        case 'ajouter': {
            const { lire, resolue } = lecteurDeConstruction(apres, enonce);
            const finaux = finauxDuLot(c.objets);
            const tousPoints = finaux.every((o) => resolue.objets.get(o.id)?.genre === 'point');
            const morceaux = finaux.map(lire).filter((t) => t !== '');
            if (morceaux.length === 0)
                return 'Ajoute un objet.';
            return `${tousPoints ? 'Place' : 'Trace'} ${enListe(morceaux)}.`;
        }
        case 'deplacer': {
            const qui = designerObjet(apres, c.id, enonce) ?? 'un point';
            const p = lecteurDeConstruction(apres, enonce).resolue.objets.get(c.id);
            return p?.genre === 'point'
                ? `Déplace ${qui.replace(/ \(.*\)$/u, '')} en ${coordonneesLues(p.p)}.`
                : `Déplace ${qui}.`;
        }
        case 'modifier': {
            if ((c.avant.nom ?? '') !== (c.apres.nom ?? '')) {
                const ancien = designerObjet(avant, c.id, enonce) ?? 'un objet';
                return c.apres.nom ? `Appelle « ${c.apres.nom} » ${ancien}.` : `Retire le nom de ${ancien}.`;
            }
            return `Modifie ${designerObjet(apres, c.id, enonce) ?? 'un objet'}.`;
        }
        case 'supprimer': {
            const noms = c.objets
                .map((x) => designerObjet(avant, x.objet.id, enonce))
                .filter((t) => t !== null);
            return noms.length === 0 ? 'Efface un objet.' : `Efface ${enListe(noms)}.`;
        }
    }
}
function idsTouches(c) {
    switch (c.type) {
        case 'ajouter':
        case 'partir_de_l_enonce':
            return c.objets.map((o) => o.id);
        case 'supprimer':
            return c.objets.map((o) => o.objet.id);
        case 'deplacer':
        case 'modifier':
            return [c.id];
    }
}
/**
 * La construction de l'élève, en étapes. Avec un historique qui redonne la
 * construction soumise : une étape par commande. Sans (un essai d'avant L7,
 * ou un historique écarté) : une étape par objet, dans l'ordre de création —
 * l'écran le dit.
 */
export function constructionDeLEleve(final, historique, enonce) {
    const rejoue = historique === null || historique === undefined || historique.length === 0
        ? null
        : rejouerHistorique(final, historique, enonce);
    if (rejoue !== null) {
        const etapes = rejoue.commandes.map((c, i) => {
            const avant = i === 0 ? rejoue.depart : rejoue.etats[i - 1];
            const apres = rejoue.etats[i];
            return {
                rang: i + 1,
                genre: c.type,
                phrase: phraseDeCommande(c, avant, apres, enonce),
                document: apres,
                touches: idsTouches(c),
                changeLaFigure: c.type === 'modifier'
                    ? JSON.stringify(c.avant.def) !== JSON.stringify(c.apres.def)
                    : c.type !== 'supprimer',
            };
        });
        return { avecHistorique: true, depart: rejoue.depart, etapes };
    }
    /* Objet par objet : un objet cité (les deux points d'une droite) vient avec lui. */
    const objets = Array.isArray(final?.objets) ? final.objets : [];
    const { lire, resolue } = lecteurDeConstruction(final, enonce);
    const etapes = [];
    objets.forEach((o, i) => {
        if (o.masque === true || NON_DESIGNABLES.has(o.def.type))
            return;
        const quoi = lire(o);
        if (quoi === '')
            return;
        const verbe = resolue.objets.get(o.id)?.genre === 'point' ? 'Place' : 'Trace';
        etapes.push({
            rang: etapes.length + 1,
            genre: 'objet',
            phrase: `${verbe} ${quoi}.`,
            document: { ...final, objets: objets.slice(0, i + 1) },
            touches: [o.id],
            changeLaFigure: true,
        });
    });
    return { avecHistorique: false, depart: { ...final, objets: [] }, etapes };
}
/** L'objet et ceux dont il dépend, dans la construction soumise. */
function avecAscendants(doc, id) {
    const parId = new Map(doc.objets.map((o) => [o.id, o]));
    const vus = new Set([id]);
    const pile = [id];
    while (pile.length > 0) {
        const o = parId.get(pile.pop());
        if (o === undefined)
            continue;
        for (const ref of referencesDe(o.def)) {
            if (!vus.has(ref.id) && parId.has(ref.id)) {
                vus.add(ref.id);
                pile.push(ref.id);
            }
        }
    }
    return vus;
}
/**
 * L'étape où la construction a DÉVIÉ : la dernière qui a posé, déplacé ou
 * redéfini l'objet retenu pour la première contrainte manquée — ou un objet
 * dont il dépend (un nom donné ne compte pas : il ne change pas la figure). Au-delà, rien ne l'a plus changé : c'est là qu'il a pris la
 * forme que le vérificateur n'accepte pas. `null` : aucun objet en cause
 * (la contrainte manque faute d'objet), ou aucune étape ne le touche.
 */
export function etapeDeDeviation(construction, final, objetFautif) {
    if (objetFautif === null)
        return null;
    const enCause = avecAscendants(final, objetFautif);
    for (let i = construction.etapes.length - 1; i >= 0; i -= 1) {
        const e = construction.etapes[i];
        if (!e.changeLaFigure)
            continue;
        if (e.touches.some((id) => enCause.has(id)))
            return e.rang;
    }
    return null;
}
/** « Étape 3 sur 7 : … » — la phrase d'une étape, pour un lecteur d'écran. */
export function direEtapeDeLEleve(e, total) {
    return `Étape ${e.rang} sur ${total} : ${majuscule(e.phrase)}`;
}
//# sourceMappingURL=construction-rejouee.js.map